import { classnames, Icon, Text } from '@terveystalo/design-system-react-components';
import { sortedUniq } from 'lodash';
import { ApiModelType } from 'model/api';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoPage } from 'ui/common/InfoPage';
import { StartPage } from 'ui/common/StartPage';
import { PreForm } from 'ui/questionary/PreForm';
import { QuestionnaireForm } from 'ui/questionary/QuestionnaireForm';
import { SummaryPage } from 'ui/questionary/SummaryPage';
import { useApiData } from 'utils/api/helpers';
import { isEmbeddedToPRSP, isEmbeddedToTerveystaloOnline } from 'utils/communication/config';
import { sendAppCompleted, sendAppError, sendMessage } from 'utils/communication/useSendMessages';
import { useUiContext } from 'utils/react/ui-context';
import { ContentPage, Language, ownApiAnswer } from 'utils/types/common';

export const PageWrapper: FC = () => {
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.language as Language;
  const [activePage, setActivePage] = useState<ContentPage>('start');
  const [keywordsData] = useApiData('getKeywords', [selectedLanguage]);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [session, setSession] = useState<string>('');
  const [answers, setAnswers] = useState<ownApiAnswer[]>([]);
  const [secureMetaData, setSecureMetaData] = useState<string | undefined>();
  const [encryptedMetaData, setEncryptedMetaData] = useState<
    ApiModelType['EncryptedMetaDataResponse']['encryptedMetadata'] | undefined
  >();
  const { config } = useUiContext();
  const keywordsDataWithoutDublicates =
    keywordsData.status === 'REMOTE_DATA_SUCCESS'
      ? {
          ...keywordsData,
          data: {
            ...keywordsData.data,
            keywords: sortedUniq(keywordsData.data.keywords), // TODO: This should be done in the backend
          },
        }
      : keywordsData;

  const handleSymptomAddition = (symptom: string) => {
    // Don't add duplicate keywords
    if (!selectedSymptoms.includes(symptom)) {
      setSelectedSymptoms(selectedSymptoms.concat(symptom));
    }
  };

  const handleSymptomReduction = (symptom: string) => {
    setSelectedSymptoms(selectedSymptoms.filter(s => s !== symptom));
  };

  const handleSessionUpdate = (session: string) => {
    setSession(session);
  };

  const handleSecureMetaDataUpdate = (data: ApiModelType['ChatMetaDataResponse']) => {
    setSecureMetaData(data.secureMetadata ?? undefined);
  };

  const handleEncryptedMetaDataUpdate = (data: ApiModelType['EncryptedMetaDataResponse']) => {
    setEncryptedMetaData(data.encryptedMetadata ?? undefined);
  };

  const handleCompletedMessage = () => {
    // Send error instead of completed if metadata is empty
    if (!!secureMetaData && !isEmbeddedToPRSP) {
      sendAppCompleted(secureMetaData, config.CHAT_QUEUE_ID);
      sendMessage({ type: 'Completed', payload: { metadata: secureMetaData, chatQueueId: config.CHAT_QUEUE_ID } });
    } else if (!!encryptedMetaData && isEmbeddedToPRSP) {
      sendMessage({ type: 'CompletedEncrypted', payload: { encryptedData: encryptedMetaData } });
    } else {
      sendAppError('No metadata available');
      sendMessage({ type: 'Error', payload: { type: 'InternalError', message: 'No metadata available' } });
    }
  };

  const continueToSummary = (answers: ownApiAnswer[]) => {
    setAnswers(answers);
    setActivePage('summary');
  };

  return (
    <div className={classnames('my-2', 'mx-auto', 'max-w-md', 'px-1.5')}>
      {/* In TT Online and PRSP we don't need to show the titles as the modal includes them */}
      {!(isEmbeddedToTerveystaloOnline || isEmbeddedToPRSP) && (
        <div className={classnames('mb-0.5')}>
          <p className={classnames('text-body-m font-bold text-zodiac-black-65')}>
            {activePage === 'start' ? t('digichat247') : t('chat_preliminary_questionaire')}
          </p>
        </div>
      )}
      {activePage === 'start' && <StartPage onClickNext={() => setActivePage('info')} />}
      {activePage === 'info' && <InfoPage onClickNext={() => setActivePage('pre-form')} />}
      {activePage === 'pre-form' && (
        <PreForm
          onClickNext={() => setActivePage('form')}
          handleSymptomAddition={handleSymptomAddition}
          handleSymptomReduction={handleSymptomReduction}
          selectedSymptoms={selectedSymptoms}
          keywordsData={keywordsDataWithoutDublicates}
        />
      )}
      {activePage === 'form' && (
        <QuestionnaireForm
          onClickNext={continueToSummary}
          selectedSymptoms={selectedSymptoms}
          handleSessionUpdate={handleSessionUpdate}
          session={session}
        />
      )}
      {activePage === 'summary' && (
        <SummaryPage
          onClickNext={() => handleCompletedMessage()}
          handleSecureMetaDataUpdate={handleSecureMetaDataUpdate}
          handleEncryptedMetaDataUpdate={handleEncryptedMetaDataUpdate}
          session={session}
          answers={answers}
        />
      )}
      {activePage !== 'start' && (
        <button id="back-to-start" type="button" className={classnames('flex')} onClick={() => setActivePage('start')}>
          <Icon name="ArrowLeft" className="mr-1" />
          <Text size="m" color="matisseBlue">
            {t('return_to_start')}
          </Text>
        </button>
      )}
    </div>
  );
};
